"use client";

import { useEffect, useRef, useState } from "react";
import { Paper, Text, Badge, Button, TextInput, Modal, Stack, Group, Title } from "@mantine/core";
import { useParams } from "next/navigation";
import { PROMOTE_DRAPE_KIT } from "@/lib/websiteOffers";
import { trackGizmo } from "@/lib/analytics";

type Props = {
  placement: string;
  locale?: string;
};

function useDrapeKitWaitlist({ placement, locale }: Props) {
  const params = useParams();
  const paramLocale = typeof params?.locale === "string" ? params.locale : undefined;
  const resolvedLocale = locale ?? paramLocale ?? "en";

  const [opened, setOpened] = useState(false);
  const [email, setEmail] = useState("");
  const [state, setState] = useState<"idle" | "loading" | "done" | "error">("idle");
  const containerRef = useRef<HTMLDivElement>(null);
  const viewedRef = useRef(false);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !viewedRef.current) {
            viewedRef.current = true;
            trackGizmo("kit_card_view", { placement });
            observer.disconnect();
          }
        });
      },
      { threshold: 0.5 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [placement]);

  const handleCardClick = () => {
    trackGizmo("kit_card_click", { placement });
    setOpened(true);
  };

  const handleSubmit = async () => {
    if (!email.includes("@") || state === "loading") return;
    setState("loading");
    try {
      const res = await fetch("/api/quiz-lead", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, source: "drape_kit_waitlist", resultLabel: placement, locale: resolvedLocale }),
      });
      if (!res.ok) throw new Error("failed");
      trackGizmo("kit_waitlist_signup", { placement });
      setState("done");
    } catch {
      setState("error");
    }
  };

  return {
    resolvedLocale,
    opened,
    setOpened,
    email,
    setEmail,
    state,
    containerRef,
    handleCardClick,
    handleSubmit,
  };
}

function DrapeKitWaitlistModal({
  opened,
  onClose,
  state,
  email,
  setEmail,
  handleSubmit,
}: {
  opened: boolean;
  onClose: () => void;
  state: "idle" | "loading" | "done" | "error";
  email: string;
  setEmail: (email: string) => void;
  handleSubmit: () => void;
}) {
  return (
    <Modal
      opened={opened}
      onClose={onClose}
      size="lg"
      centered
      overlayProps={{
        backgroundOpacity: 0.7,
        blur: 3,
      }}
      styles={{
        content: { borderRadius: 16 },
      }}
    >
      {state === "done" ? (
        <Text size="sm" py="md">
          You&apos;re on the list — 20% off is locked in. ✓
        </Text>
      ) : (
        <Stack gap="sm">
          <Text fw={700} size="lg">
            First batch is in production
          </Text>
          <Text size="sm" c="dimmed">
            Drop your email and lock in 20% off launch pricing — we&apos;ll email you the moment kits ship.
          </Text>
          <Group gap="xs" wrap="nowrap">
            <TextInput
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.currentTarget.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
              style={{ flex: 1 }}
              size="sm"
              error={state === "error" ? "Something went wrong — try again" : undefined}
            />
            <Button size="sm" color="dark" onClick={handleSubmit} loading={state === "loading"}>
              Join the waitlist
            </Button>
          </Group>
        </Stack>
      )}
    </Modal>
  );
}

export default function DrapeKitCard({ placement, locale }: Props) {
  const {
    resolvedLocale,
    opened,
    setOpened,
    email,
    setEmail,
    state,
    containerRef,
    handleCardClick,
    handleSubmit,
  } = useDrapeKitWaitlist({ placement, locale });

  if (!PROMOTE_DRAPE_KIT || !resolvedLocale.startsWith("en")) return null;

  return (
    <>
      <Paper ref={containerRef} radius="lg" p="lg" style={{ background: "#faf7f2", borderTop: "3px solid #141414" }}>
        <Stack gap={8}>
          <Badge size="sm" color="dark" variant="filled" style={{ alignSelf: "flex-start" }}>
            NEW · FIRST BATCH
          </Badge>
          <Text fw={700} size="lg" style={{ letterSpacing: -0.2 }}>
            At-Home Draping Kit — $59
          </Text>
          <Text size="sm" c="dimmed" lh={1.55}>
            48 professional drape cards for every season, a guided photo session on this site, and your final side-by-side color report. Ships free (US).
          </Text>
          <Button fullWidth mt={4} styles={{ root: { background: "#141414" } }} onClick={handleCardClick}>
            Get the kit →
          </Button>
        </Stack>
      </Paper>

      <DrapeKitWaitlistModal
        opened={opened}
        onClose={() => setOpened(false)}
        state={state}
        email={email}
        setEmail={setEmail}
        handleSubmit={handleSubmit}
      />
    </>
  );
}

function DrapeKitFanSvg() {
  const cards: { deg: number; fill: string }[] = [
    { deg: -22, fill: "#3B5998" },
    { deg: 22, fill: "#DAA520" },
    { deg: -11, fill: "#D4A5A5" },
    { deg: 11, fill: "#B03A78" },
    { deg: 0, fill: "#0F5C36" },
  ];

  return (
    <svg viewBox="0 0 340 200" style={{ width: "100%", maxWidth: 320, height: "auto", display: "block" }} aria-hidden="true">
      {cards.map(({ deg, fill }) => {
        const isFront = deg === 0;
        return (
          <g key={deg} transform={`rotate(${deg} 170 230)`} style={{ filter: "drop-shadow(0 4px 8px rgba(0,0,0,0.22))" }}>
            <rect x={115} y={30} width={110} height={154} rx={8} fill={fill} />
            <ellipse cx={170} cy={88} rx={26} ry={34} fill="#faf7f2" stroke="rgba(0,0,0,0.10)" strokeWidth={1} />
            {isFront && (
              <>
                <rect x={122} y={37} width={16} height={16} rx={2} fill="#fff" />
                <rect x={125} y={40} width={10} height={10} fill="#141414" />
                <rect x={123} y={156} width={94} height={20} rx={4} fill="#fff" />
                <text x={170} y={169} textAnchor="middle" fontSize="8" fontWeight="700" letterSpacing="0.5" fill="#141414">
                  DEEP EMERALD
                </text>
              </>
            )}
          </g>
        );
      })}
    </svg>
  );
}

export function DrapeKitShowcase({ placement, locale }: Props) {
  const { resolvedLocale, opened, setOpened, email, setEmail, state, containerRef, handleCardClick, handleSubmit } =
    useDrapeKitWaitlist({ placement, locale });

  if (!PROMOTE_DRAPE_KIT || !resolvedLocale.startsWith("en")) return null;

  return (
    <>
      <Paper ref={containerRef} p="xl" radius="lg" style={{ background: "#faf7f2", borderTop: "3px solid #141414" }} h="100%">
        <Stack gap="md" align="center" h="100%" justify="space-between">
          <Stack gap="xs" align="center">
            <Badge color="dark" variant="filled" size="lg">
              Physical Kit · First Batch
            </Badge>
            <DrapeKitFanSvg />
            <Title order={3} ta="center" fw={700}>
              Now Hold the Real Thing
            </Title>
            <Text ta="center" c="dimmed" fz="sm" maw={340}>
              48 pro drape cards — the four signature colors of every season you just
              tried on screen. Drape them in daylight, follow the guided photo
              session, and settle your season for good.
            </Text>
          </Stack>
          <Stack gap={6} w="100%" align="center">
            <Button size="lg" fullWidth styles={{ root: { background: "#141414" } }} onClick={handleCardClick}>
              Get the Drape Kit — $59
            </Button>
            <Text fz="xs" c="dimmed" ta="center">
              48 cards · all 12 seasons · guided photo tool · free US shipping
            </Text>
          </Stack>
        </Stack>
      </Paper>

      <DrapeKitWaitlistModal
        opened={opened}
        onClose={() => setOpened(false)}
        state={state}
        email={email}
        setEmail={setEmail}
        handleSubmit={handleSubmit}
      />
    </>
  );
}
